/**
 * Languages we support as first-class citizens across the product:
 * UI ordering, TTS voices, STT models, and any future language-specific
 * features should be keyed off this list.
 *
 * Adding a language here is a deliberate, cross-cutting decision — every
 * consumer that branches on `SupportedLanguage` will fail to compile until
 * it handles the new entry.
 */
export const SUPPORTED_LANGUAGES = ['nl', 'en', 'fr'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Narrow an arbitrary ISO 639-1 code to one of our supported languages,
 * falling back to `DEFAULT_LANGUAGE` for anything we don't ship features for.
 */
export function toSupportedLanguage(value: string | null | undefined): SupportedLanguage {
  return value && isSupportedLanguage(value) ? value : DEFAULT_LANGUAGE;
}

/**
 * Who settled a user's language. Only `default` — nobody did — is replaced
 * automatically, by the first contact's guess (`teams` or `browser`). `staff`
 * and `user` are human choices; `unknown` is every account older than this
 * column, which is treated as final.
 */
export const LANGUAGE_SOURCES = [
  'default',
  'teams',
  'browser',
  'staff',
  'user',
  'unknown',
] as const;

export type LanguageSource = (typeof LANGUAGE_SOURCES)[number];
